<template>
  <div class="card">
    <div class="card-header bg-primary d-flex justify-content-between">
      <h5 class="mb-0 text-white">Most Frequent Disclosures</h5>
    </div>
    <div class="card-body">
      <ErrorSummary :error="error" />

      <!-- Explanation Text -->
      <div class="alert alert-info mb-4" role="alert">
        <div class="mb-2">
          To highlight high level patterns of risk and concerning behavior, the information below displays the most frequent disclosures by employees who took
          the questionnaire as part of this investigation. Questions listed are those answered in a way that reveals admissions or observations of
          counterproductive workplace behavior and are in order from most frequent to least frequent disclosures.
        </div>
        <div>
          Please note the Verensics questionnaire is dynamic and questions are presented based on multiple factors, and therefore not every question is
          presented to every employee.
        </div>
      </div>

      <div v-if="isLoading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading most frequent disclosures...</p>
      </div>
      <div v-else-if="categories.length === 0" class="alert alert-light text-center" role="alert">
        No disclosures data available
      </div>
      <div v-else-if="categories.length > 0" class="container-fluid">
        <div class="row">
          <div class="col-12 col-md-4 px-0 border-right bg-light d-print-none">
            <table class="table table-hover border-bottom bg-white">
              <tbody>
                <tr
                  v-for="category in categories"
                  :key="category.code"
                  :class="{ 'table-active': selectedCategory == category }"
                  @click="selectedCategoryCode = category.code"
                >
                  <td class="p-0 position-relative">
                    <div class="d-flex justify-content-center border-0">
                      <CategoryIcon class="px-3 py-1" :categoryCode="category.code" />
                    </div>
                  </td>
                  <td>{{ category.displayName }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="selectedCategory" class="col">
            <div class="row mt-2">
              <div class="mt-2 ml-3">
                <CategoryIcon :categoryCode="selectedCategory.code" />
              </div>
              <div class="col mt-2">
                <h4>{{ selectedCategory.displayName }}</h4>
              </div>
            </div>
            <div class="row d-print-none">
              <div class="col">
                <div class="card mt-3" v-for="response in questionsToView" :key="response.responseId">
                  <div class="card-body py-2">
                    <div class="row">
                      <div class="col">
                        <div class="row">
                          <div class="col">
                            <p class="font-weight-bold mb-2">{{ response.text }}</p>
                            <div class="text-muted small mb-2">
                              {{ response.nonPreferredCount }} of {{ response.totalAnsweredCount }} respondents ({{ response.nonPreferredPercentage }}%)
                              answered {{ response.nonPreferredResponseText }}, skipped this question, or timed out.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer py-2" v-if="response.reports && response.reports.length > 0">
                    <div class="row">
                      <div class="col">
                        <div class="d-flex align-items-center">
                          <button
                            class="btn btn-sm btn-outline-secondary"
                            type="button"
                            data-toggle="collapse"
                            :data-target="`#reports-${response.responseId}`"
                            :aria-expanded="false"
                            :aria-controls="`reports-${response.responseId}`"
                          >
                            <span class="collapse-text">Toggle</span> Reports ({{ response.reports.length }})
                          </button>
                        </div>
                        <div :id="`reports-${response.responseId}`" class="collapse">
                          <div class="row mt-2">
                            <div
                              v-for="(reports, responseType) in groupedReports(response.reports, response.nonPreferredResponseText)"
                              :key="responseType"
                              :class="getColumnClass(response.reports, response.nonPreferredResponseText)"
                              class="report-column"
                            >
                              <h6 class="text-capitalize mb-2">{{ responseType }} ({{ reports.length }})</h6>
                              <div v-for="report in reports" :key="report.organizationReportId" class="d-flex align-items-center mb-1">
                                <span @click="onStarClick(report)" class="star-icon" v-if="report.organizationReportId">
                                  <fa class="gold-star" :icon="['fas', 'star']" fixed-width v-if="report.isStarred" />
                                  <fa :icon="['far', 'star']" fixed-width v-if="!report.isStarred" />
                                </span>
                                <a
                                  :href="`/organizations/${organizationId}/reports/${report.organizationReportId}`"
                                  class="text-decoration-none report-link"
                                  target="_blank"
                                >
                                  {{ report.registrationId || "Report #" + report.organizationReportId }}
                                  <span v-if="report.count > 1" class="text-muted small ml-1">({{ report.count }})</span>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row d-none d-print-inline">
              <div class="col px-0">
                <table class="table mt-1" v-if="hasQuestionsToViewInCategory">
                  <thead>
                    <tr>
                      <th class="text-left">Question</th>
                      <th class="text-center">Non-preferred Response Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="response in questionsToView" :key="response.responseId">
                      <td class="text-left">
                        <span>{{ response.text }}</span>
                      </td>
                      <td class="text-center">{{ response.nonPreferredPercentage }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CategoryIcon from "./CategoryIcon.vue";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    CategoryIcon,
    ErrorSummary,
  },
  props: {
    isLoading: Boolean,
    organizationId: String,
    investigationId: String,
    investigation: Object,
  },
  data() {
    return {
      error: null,
      selectedCategoryCode: null,
    };
  },
  computed: {
    categories() {
      return this.investigation?.riskIndexAnalysis?.categories || [];
    },
    selectedCategory() {
      if (!this.categories || !this.categories.length) return null;
      return this.categories.find(c => c.code === this.selectedCategoryCode) ?? this.categories[0];
    },
    hasQuestionsToViewInCategory() {
      return this.questionsToView.length > 0;
    },
    questionsToView() {
      return this.selectedCategory?.questions?.filter(q => q.nonPreferredCount > 0) || [];
    },
    groupedReports() {
      return (reports, nonPreferredResponseText) => {
        const nonPreferredKey = `Answered ${nonPreferredResponseText}`;
        const grouped = {
          [nonPreferredKey]: {},
          skip: {},
          timeout: {},
        };
        if (!reports) return {};
        reports.forEach(report => {
          let key = report.responseType;
          if (key === "non-preferred") {
            key = nonPreferredKey;
          }
          if (grouped[key] !== undefined) {
            const reportId = report.organizationReportId;
            if (grouped[key][reportId]) {
              // Increment count for duplicate
              grouped[key][reportId].count++;
            } else {
              // First occurrence, add with count = 1
              grouped[key][reportId] = {
                ...report,
                count: 1,
              };
            }
          }
        });
        // Convert to arrays and only return groups that have elements
        const filteredGroups = {};
        Object.keys(grouped).forEach(responseType => {
          const reportArray = Object.values(grouped[responseType]);
          if (reportArray.length > 0) {
            filteredGroups[responseType] = reportArray;
          }
        });
        return filteredGroups;
      };
    },
  },
  watch: {
    selectedCategory() {
      if (this.categories && this.categories.length > 0 && !this.selectedCategory) {
        this.selectedCategoryCode = this.categories[0].code;
      }
    },
  },
  created() {
    this.selectedCategoryCode = this.$route.query.cc;
  },
  methods: {
    getColumnClass(reports, nonPreferredResponseText) {
      const groupedReports = this.groupedReports(reports, nonPreferredResponseText);
      const numColumns = Object.keys(groupedReports).length;
      if (numColumns === 1) return "col-md-12";
      if (numColumns === 2) return "col-md-6";
      return "col-md-4";
    },
    onStarClick(report) {
      if (!report || !report.organizationReportId) return;

      // Flip the star state
      report.isStarred = !report.isStarred;

      // save to server
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/reports/${report.organizationReportId}/star/${report.isStarred.toString()}`)
        .then(() => {
          // emit the star update event to synchronize with other components
          this.$emit("star-updated", {
            organizationReportId: report.organizationReportId,
            isStarred: report.isStarred,
            registrationId: report.registrationId,
          });
        })
        .catch(err => {
          this.error = err;
          // revert on error
          report.isStarred = !report.isStarred;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .mb-0;

  tr {
    cursor: pointer;
  }

  th,
  td {
    @extend .text-center;
    @extend .align-middle;

    h4,
    h5 {
      @extend .mb-0;
    }
  }
}

.star-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.gold-star {
  color: #ffd700;
}

.report-column {
  padding-right: 15px;
  &:last-child {
    padding-right: 0;
  }
}

.report-link {
  word-break: break-all;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.2;
  max-width: 100%;
  display: inline-block;
}
</style>
